"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Container } from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useModal } from "@/contexts/ModalContext";
import { detectPlatform, trackDownloadClick } from "@/lib/utils";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about-us", label: "About Us" },
  { href: "/blog", label: "Blog" },
  { href: "/real-estate-social-networking-app-in-uae", label: "Realty Networking Solution" },
  { href: "/contact", label: "Contact Us" },
];

export function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [platform, setPlatform] = useState<"android" | "ios" | "web">("web");
  const pathname = usePathname();
  const { openModal } = useModal();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    setPlatform(detectPlatform());
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const handleGetStarted = () => {
    if (platform === "android") {
      trackDownloadClick("android");
      window.open("https://play.google.com/store/apps/details?id=com.rem.realtorsIn", "_blank");
    } else if (platform === "ios") {
      trackDownloadClick("ios");
      window.open("https://apps.apple.com/pk/app/realtorsin/id6752862402", "_blank");
    } else {
      openModal();
    }
  };

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full max-w-full ${
        isScrolled
          ? "bg-white/95 backdrop-blur-lg shadow-lg border-b border-neutral-200"
          : "bg-white/90 backdrop-blur-md"
      }`}
      style={
        isScrolled
          ? { backgroundColor: "rgba(255,255,255,0.98)" }
          : { backgroundColor: "rgba(255,255,255,0.95)" }
      }
    >
      <Container>
        <nav className="flex items-center justify-between h-20 min-w-0 overflow-hidden">
          {/* Logo */}
          <Link
            href="/"
            prefetch={false}
            className="flex items-center space-x-2 flex-shrink-0"
          >
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center p-1.5 shadow-sm">
              <img src="/assets/brand/realtorsin-logo.svg" alt="RealtorsIn Logo" className="w-full h-full" />
            </div>
            <span
              className="text-2xl font-bold"
              style={{
                background: "linear-gradient(to right, #2BA6E6, #112F6B)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              } as React.CSSProperties}
            >
              RealtorsIn
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                prefetch={false}
                className="relative px-3 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                style={{
                  color: isActive(link.href) ? "#0B76B7" : "#374151",
                }}
              >
                {link.label}
                {isActive(link.href) && (
                  <motion.div
                    layoutId="activeUnderline"
                    className="absolute bottom-1 left-3 right-3 h-0.5 rounded-full"
                    style={{ backgroundColor: "#2BA6E6" }}
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center">
            <Button
              size="default"
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
              style={{ backgroundColor: "#0B76B7" }}
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-neutral-700 hover:text-primary-600 transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-4 space-y-1 border-t border-neutral-200">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    prefetch={false}
                    className="flex items-center px-3 py-2.5 rounded-lg font-medium transition-colors"
                    style={{
                      color: isActive(link.href) ? "#0B76B7" : "#374151",
                      backgroundColor: isActive(link.href) ? "rgba(43,166,230,0.06)" : "transparent",
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-3 pb-1">
                  <Button
                    onClick={handleGetStarted}
                    className="w-full bg-gradient-to-r from-primary-600 to-primary-700"
                    style={{ backgroundColor: "#0B76B7" }}
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </motion.header>
  );
}
